<template lang="pug">
.unit-start.flex.items-stretch.justify-center.relative.z-0.col-span-text.flex-col.gap-12.max-w-full.min-h-uc-screen(
  uc-fade=""
)
  //- tag info for word doc (don't duplicate if both the unit and unit-title screen both have tags)
  div(
    uc-dont-read=""
    uc-word-only=""
    v-if="unit?.tag && !screen?.tag"
  )
    //- expose for translations
    //- text="[ ${unitLabel}: Dynamic content ]"
    uc-text(
      :values="{ unitLabel: unitLabel }"
      :text="`[ ${unitLabel}: Dynamic content ]`"
      type="p"
      uc-word-italic=""
    )
    //- expose for translations
    //- text="'Any **${count}** of the following:'"
    uc-text(
      :values="{ count: unit?.tag.any }"
      :text="'Any **${count}** of the following:'"
      type="p"
      uc-word-italic=""
      v-if="unit?.tag.any"
    )
    ul
      li(v-for="tag in getTags(unit)")
        uc-text(
          :text="Boolean(tagDescription(tag)) ? `**${tag}** (${tagDescription(tag)})` : `**${tag}**`"
          uc-word-italic=""
        )

  .flex.flex-col.flex-1.gap-12.justify-center
    //- text="Unit ${unitNumber}"
    //- omit from audio because unit number is dynamic
    uc-text.unit-subtitle(
      :data-unit-number="screen.id"
      :k="unit && unit.number && 'Unit ${unitNumber}'"
      :text="unit && unit.number && '${unitLabel} ${unitNumber}'"
      :values="{ unitNumber: unit.number, unitLabel: unitLabel }"
      type="p"
      uc-dont-read=""
      v-if="unit && unit.number && showUnitLabel"
    )

    //- TODO solve patches for page titles
    uc-editable.unit-title(
      :data-unit-title="screen.id"
      :editPath="`#${pageId}.title`"
      :k="`${pageId}.title`"
      :text="pageTitle"
      type="h1"
    )

  .text-center.my-12(v-if="!wordDoc && !audioDoc")
    uc-preload-sfx(name="continue")
    uc-button.btn.btn-lg.btn-primary.unlock.ripple(
      v-if="screen.start || moreButtons"
    )
      uc-text(text="Start")
    button.scroll-down-button.inline-block.text-white.p-2.focus-visible_ring(
      @click="scrollDown"
      data-sfx="continue"
      :aria-label="hasTranslation('Continue')"
      v-else=""
    )
      span.scroll-down-icon-wrapper.text-center.inline-block.text-6xl.mb-4.block(:class="{ 'animate-bounce': !suspend.reducedMotion }")
        uc-icon.scroll-down-icon.text-primary-300(icon="theme:down")
      span.scroll-down-text-wrapper.text-center.prose-stack.font-semibold.title-scroll-text.block(
        :style="{ 'text-shadow': screen['no-overlay'] ? null : 'rgb(var(--color-gray-800) / 50%) 0px 0px 10px, rgb(var(--color-gray-800) / 50%) 0px 0px 15px, rgb(var(--color-gray-800) / 50%) 0px 0px 20px' }"
      )
        uc-text.tracking-wide(
          k="uc.6017541529969642"
          text="Scroll to continue"
        )
</template>

<script lang="ts">
import gsap from 'gsap';
import { defineComponent, unref, toRef } from 'vue';
import useTranslations from '../stores/useTranslations';
import useSuspend from '../lib/useSuspend';
import getTags, { tagDescription } from '../lib/getTags';
import useScreenAudio from '../lib/useScreenAudio';
import unitTitleSchema from '../lib/screen-schemas/screen-unit-title.schema';

export default defineComponent({
  inject: [
    'content',
    'pageId',
    'pageTitle',
    'unit',
    'moreButtons',
    'audioDoc',
    'wordDoc',
    'sectionActive'
  ],
  props: {
    screen: { type: Object, required: true },
    isSubunit: { type: Boolean, required: false, default: false }
  },
  setup(props) {
    const suspend = useSuspend();
    const { translations } = useTranslations();
    const { playSlot } = useScreenAudio(
      toRef(props, 'screen'),
      unitTitleSchema
    );

    return { suspend, translations, getTags, tagDescription, playSlot };
  },
  watch: {
    // Replay the unit-title narration (and stop any audio still playing from a
    // later screen) whenever the learner scrolls back to the unit title. The
    // intro animation only fires playAudio on mount / restartTitleAnim, so
    // without this, returning here never calls play() and the previous screen's
    // narration keeps playing. Mirrors screen-unit-end.vue. (First section per
    // page, so this never fires on initial load — the mount animation handles
    // that; sectionActive's computed only re-evaluates on currentSection change.)
    sectionActive(active) {
      if (active) this.playAudio();
    }
  },
  computed: {
    unitLabel() {
      return this.content.course['unit-label'] || 'Unit';
    },
    showUnitLabel() {
      return this.content.course['hide-unit-labels'] ? false : true;
    }
  },
  mounted() {
    this.fadeInScreen();

    const app = document.querySelector('#app');
    app?.addEventListener('restartTitleAnim', async e => {
      this.fadeInScreen();
    });
  },
  methods: {
    playAudio() {
      // check if the active section has changed
      if (!this.sectionActive) return;

      const id = unref(this.pageId);
      const text = unref(this.pageTitle) || undefined;
      this.playSlot('intro', text, `${id}.title`);
    },
    fadeInScreen() {
      const { id } = this.screen;

      if (!this.screen['no-fade']) {
        if (this.suspend.reducedMotion) {
          gsap.set(`[data-page-background="${this.pageId}"]`, {
            scale: 1,
            blur: 10
          });
          gsap.set('.bottom-left-logo', { autoAlpha: 1 });
          gsap.set(`[data-screen-fill="${id}"]`, { opacity: 1 });
          gsap.set(
            `[data-unit-number="${id}"], [data-unit-title="${id}"], [data-scroll-down="${id}"]`,
            { opacity: 1 }
          );

          this.playAudio();
          return;
        }

        gsap
          .timeline()
          .fromTo(
            `[data-page-background="${this.pageId}"]`,
            { scale: 1.3 },
            {
              scale: 1,
              duration: 10,
              ease: 'expo'
            }
          )
          .to('.bottom-left-logo', { autoAlpha: 1 }, 1)
          .fromTo(
            `[data-page-background="${this.pageId}"]`,
            { blur: 0 },
            {
              blur: 10,
              duration: 15,
              ease: 'expo'
            },
            1
          )
          .fromTo(
            `[data-screen-fill="${id}"]`,
            { opacity: 0, blur: 0 },
            {
              opacity: 1,
              duration: 10,
              ease: 'expo'
            },
            1
          )
          // .fromTo(
          //   `[data-backdrop="${id}"]`,
          //   { opacity: 0 },
          //   {
          //     opacity: 1,
          //     duration: 2,
          //     ease: 'linear'
          //   },
          //   0.5
          // )
          .fromTo(
            `[data-unit-number="${id}"], [data-unit-title="${id}"], [data-scroll-down="${id}"]`,
            { opacity: 0 },
            { opacity: 1, duration: 1.5, ease: 'linear', stagger: 0.5 },
            1
          )
          .call(this.playAudio, undefined, '>0.1');
      } else {
        this.playAudio();
      }
    },
    scrollDown() {
      const el = this.$el;
      const page = el?.closest('.page');
      page.unlockAndScroll(this.screen.id);
    },
    hasTranslation(key, fallback) {
      // get the translation if it exists
      let maybeTranslated =
        this.translations?.[this.suspend.locale]?.[key] ||
        this.translations?.[this.suspend.locale]?.[fallback];

      return maybeTranslated || key;
    }
  }
});
</script>
